// src/pages/etudiant/Statuts.jsx
// ✅ Version finale avec visualisation plein écran
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../../config';

export default function Statuts() {
  const navigate = useNavigate();
  const [statuts, setStatuts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createContent, setCreateContent] = useState('');
  const [createType, setCreateType] = useState('texte');
  const [createMedia, setCreateMedia] = useState(null);
  const [createPreview, setCreatePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [position, setPosition] = useState(null);
  const [lieu, setLieu] = useState('');
  const [selectedStatut, setSelectedStatut] = useState(null);
  
  const fileInputRef = useRef(null);

  const API_BASE = config.apiBase;
  const getToken = () => localStorage.getItem('token');
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  // Récupérer la position de l'utilisateur
  const getLocation = () => {
    if (!navigator.geolocation) {
      toast.error('La géolocalisation n\'est pas supportée');
      return Promise.reject('Not supported');
    }
    
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          resolve({
            latitude: pos.coords.latitude,
            longitude: pos.coords.longitude
          });
        },
        (err) => {
          toast.error('Impossible de récupérer votre position');
          reject(err);
        },
        { enableHighAccuracy: true, timeout: 10000 }
      );
    });
  };

  // Récupérer le lieu approximatif
  const getLieuApproximatif = (lat, lng) => {
    const lieux = [
      'Campus Université', 'Bibliothèque', 'Salle de cours', 
      'Résidence étudiante', 'Restaurant universitaire', 'Amphithéâtre',
      'Laboratoire', 'Salle informatique', 'Centre sportif'
    ];
    return lieux[Math.floor(Math.random() * lieux.length)];
  };

  // Récupérer les statuts proches avec normalisation via config
  const fetchStatuts = async () => {
    try {
      const token = getToken();
      
      const pos = await getLocation();
      setPosition(pos);
      
      const lieuName = getLieuApproximatif(pos.latitude, pos.longitude);
      setLieu(lieuName);
      
      const response = await fetch(`${API_BASE}/statuts/get_statuts_proches.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({
          latitude: pos.latitude,
          longitude: pos.longitude,
          rayon: 50
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        const statutsNormalises = (data.statuts || []).map(statut => {
          let url_fichier = null;
          if (statut.url_fichier) {
            const filename = statut.url_fichier.includes('/') 
              ? statut.url_fichier.split('/').pop() 
              : statut.url_fichier;
            url_fichier = config.normalizeStatutMedia(filename);
          }
          
          let photo_profil = null;
          if (statut.photo_profil) {
            const filename = statut.photo_profil.includes('/') 
              ? statut.photo_profil.split('/').pop() 
              : statut.photo_profil;
            photo_profil = config.normalizeProfilePhoto(filename);
          }
          
          return {
            ...statut,
            url_fichier: url_fichier,
            photo_profil: photo_profil,
            distance_km: statut.distance ? Math.round(statut.distance * 10) / 10 : 0
          };
        });
        
        setStatuts(statutsNormalises);
      }
    } catch (error) {
      console.error('Erreur fetchStatuts:', error);
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  };

  // Marquer un statut comme vu
  const markAsViewed = async (id_statut) => {
    try {
      const token = getToken();
      await fetch(`${API_BASE}/statuts/vue_statut.php`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ id_statut })
      });
    } catch (error) {
      console.error('Erreur marquage vu:', error);
    }
  };

  // Créer un statut
  const handleCreateStatut = async (e) => {
    e.preventDefault();
    
    if (createType === 'texte' && !createContent.trim() && !createMedia) {
      toast.error('Écrivez quelque chose ou ajoutez un média');
      return;
    }
    
    setSubmitting(true);
    try {
      const token = getToken();
      
      let lat = position?.latitude;
      let lng = position?.longitude;
      if (!lat || !lng) {
        const pos = await getLocation();
        lat = pos.latitude;
        lng = pos.longitude;
        setPosition(pos);
      }
      
      const formData = new FormData();
      formData.append('contenu', createContent);
      formData.append('type_statut', createType);
      formData.append('latitude', lat);
      formData.append('longitude', lng);
      formData.append('lieu', getLieuApproximatif(lat, lng));
      
      if (createMedia) {
        formData.append('media', createMedia);
      }
      
      const response = await fetch(`${API_BASE}/statuts/create_statut.php`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Statut publié ! 🎉');
        setShowCreateModal(false);
        setCreateContent('');
        setCreateMedia(null);
        setCreatePreview(null);
        setCreateType('texte');
        fetchStatuts();
      } else {
        toast.error(data.message || 'Erreur lors de la publication');
      }
    } catch (error) {
      console.error('Erreur création statut:', error);
      toast.error('Erreur');
    } finally {
      setSubmitting(false);
    }
  };

  // Ouvrir le modal de statut
  const openStatutModal = (statut) => {
    setSelectedStatut(statut);
    markAsViewed(statut.id_statut);
  };

  // Lancer une discussion
  const startDiscussion = (utilisateurId) => {
    navigate('/messagerie', { state: { contactId: utilisateurId } });
  };

  // Gestion des médias
  const handleMediaSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 50 * 1024 * 1024) {
      toast.error('Fichier trop volumineux (max 50 Mo)');
      return;
    }
    
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'video/mp4', 'video/webm'];
    if (!validTypes.includes(file.type)) {
      toast.error('Type de fichier non supporté');
      return;
    }
    
    setCreateMedia(file);
    setCreateType(file.type.startsWith('video/') ? 'video' : 'image');
    
    const reader = new FileReader();
    reader.onloadend = () => setCreatePreview(reader.result);
    reader.readAsDataURL(file);
  };

  useEffect(() => {
    fetchStatuts();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[60vh]">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-500">Chargement des statuts...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-4 md:p-6">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-800">📱 Statuts</h1>
            <p className="text-sm text-gray-500">
              {lieu ? `📍 ${lieu}` : '📍 Position inconnue'}
              {position && ` • ${statuts.length} statuts à proximité`}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2.5 rounded-xl hover:from-blue-700 hover:to-indigo-700 transition flex items-center gap-2 shadow-lg shadow-blue-500/30"
          >
            <span className="text-lg">+</span> Publier
          </button>
        </div>
      </div>

      {/* Liste des statuts */}
      <div className="max-w-4xl mx-auto">
        {statuts.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-sm p-12 text-center border border-gray-100">
            <div className="text-6xl mb-4">📭</div>
            <p className="text-gray-500 text-lg">Aucun statut à proximité</p>
            <p className="text-sm text-gray-400 mt-1">Soyez le premier à publier un statut !</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {statuts.map(statut => {
              const isMine = statut.id_utilisateur === currentUser.id_utilisateur;
              return (
                <div
                  key={statut.id_statut}
                  className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-md transition-all duration-200 border ${
                    statut.deja_vu ? 'border-gray-200' : 'border-blue-300 shadow-blue-100/50'
                  }`}
                >
                  {/* Contenu du statut */}
                  <div className="relative">
                    {statut.type_statut === 'image' && statut.url_fichier && (
                      <div className="relative aspect-square bg-gray-100 cursor-pointer" onClick={() => openStatutModal(statut)}>
                        <img 
                          src={statut.url_fichier} 
                          alt="Statut" 
                          className="w-full h-full object-cover"
                          loading="lazy"
                          onError={(e) => {
                            console.error('Erreur chargement image:', statut.url_fichier);
                            e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="100" height="100"><rect width="100" height="100" fill="%23f3f4f6"/><text x="50" y="55" font-size="14" text-anchor="middle" fill="%239ca3af">🖼️</text></svg>';
                          }}
                        />
                        {!statut.deja_vu && (
                          <span className="absolute top-2 right-2 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></span>
                        )}
                      </div>
                    )}
                    
                    {statut.type_statut === 'video' && statut.url_fichier && (
                      <div className="relative aspect-square bg-black cursor-pointer" onClick={() => openStatutModal(statut)}>
                        <video 
                          src={statut.url_fichier} 
                          className="w-full h-full object-cover"
                          muted
                          onError={(e) => console.error('Erreur chargement vidéo:', statut.url_fichier)}
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/30">
                          <span className="text-6xl text-white/80">▶️</span>
                        </div>
                        {!statut.deja_vu && (
                          <span className="absolute top-2 right-2 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></span>
                        )}
                      </div>
                    )}
                    
                    {statut.type_statut === 'texte' && (
                      <div className={`p-4 min-h-[120px] flex items-center cursor-pointer ${statut.deja_vu ? 'bg-gray-50' : 'bg-gradient-to-br from-blue-50 to-indigo-50'}`} onClick={() => openStatutModal(statut)}>
                        <p className="text-gray-800 line-clamp-4 text-sm">{statut.contenu}</p>
                        {!statut.deja_vu && (
                          <span className="absolute top-2 right-2 w-3 h-3 bg-blue-500 rounded-full animate-pulse"></span>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Footer */}
                  <div className="p-3 border-t border-gray-100">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0 overflow-hidden">
                          {statut.photo_profil ? (
                            <img src={statut.photo_profil} alt="" className="w-full h-full object-cover" />
                          ) : (
                            statut.prenom?.charAt(0).toUpperCase()
                          )}
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs font-semibold text-gray-800 truncate">
                            {statut.prenom} {statut.nom}
                          </p>
                          <div className="flex items-center gap-2 text-[10px] text-gray-400">
                            <span>📍 {statut.distance_km || 0} km</span>
                            <span>•</span>
                            <span>👁️ {statut.nb_vues || 0}</span>
                          </div>
                        </div>
                      </div>
                      
                      {!isMine && (
                        <button
                          onClick={() => startDiscussion(statut.id_utilisateur)}
                          className="flex items-center gap-1 bg-blue-50 text-blue-600 px-3 py-1.5 rounded-full hover:bg-blue-100 transition text-xs font-medium whitespace-nowrap"
                        >
                          💬 Discuter
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de création */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md max-h-[90vh] overflow-y-auto p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold">📝 Nouveau statut</h2>
              <button onClick={() => setShowCreateModal(false)} className="p-1.5 hover:bg-gray-100 rounded-full transition">✕</button>
            </div>
            
            <form onSubmit={handleCreateStatut} className="space-y-4">
              <textarea
                value={createContent}
                onChange={(e) => setCreateContent(e.target.value)}
                placeholder="Quoi de neuf ?"
                rows="3"
                className="w-full border border-gray-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
              />
              
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="flex items-center gap-2 px-4 py-2 bg-gray-100 rounded-xl hover:bg-gray-200 transition text-sm"
                >
                  📎 Ajouter média
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  className="hidden"
                  onChange={handleMediaSelect}
                />
                {createMedia && (
                  <span className="text-xs text-green-600">✅ Fichier ajouté</span>
                )}
              </div>
              
              {createPreview && (
                <div className="relative rounded-xl overflow-hidden bg-gray-100 max-h-64">
                  {createType === 'image' && (
                    <img src={createPreview} alt="Aperçu" className="w-full h-auto max-h-64 object-contain" />
                  )}
                  {createType === 'video' && (
                    <video src={createPreview} controls className="w-full max-h-64" />
                  )}
                  <button
                    type="button"
                    onClick={() => { setCreateMedia(null); setCreatePreview(null); }}
                    className="absolute top-2 right-2 bg-black/70 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm hover:bg-black/90"
                  >
                    ✕
                  </button>
                </div>
              )}
              
              {position && (
                <div className="text-xs text-gray-400 flex items-center gap-1">
                  <span>📍</span> {lieu} • {position.latitude.toFixed(4)}, {position.longitude.toFixed(4)}
                </div>
              )}
              
              <button
                type="submit"
                disabled={submitting}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-700 hover:to-indigo-700 transition disabled:opacity-50 shadow-lg shadow-blue-500/25"
              >
                {submitting ? 'Publication...' : '📤 Publier'}
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ✅ Modal de visualisation du statut - PLEIN ÉCRAN */}
      {selectedStatut && (
        <div 
          className="fixed inset-0 bg-black z-50 flex items-center justify-center"
          onClick={() => setSelectedStatut(null)}
        >
          <div className="w-screen h-screen relative" onClick={(e) => e.stopPropagation()}>
            {/* Bouton fermer */}
            <button
              onClick={() => setSelectedStatut(null)}
              className="absolute top-4 right-4 z-10 bg-black/50 text-white rounded-full w-10 h-10 flex items-center justify-center hover:bg-black/70 transition"
            >
              ✕
            </button>
            
            {/* Contenu média en plein écran */}
            {selectedStatut.type_statut === 'image' && selectedStatut.url_fichier && (
              <img 
                src={selectedStatut.url_fichier} 
                alt="Statut" 
                className="w-full h-full object-contain"
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="200"><rect width="200" height="200" fill="%231f2937"/><text x="100" y="110" font-size="20" text-anchor="middle" fill="%239ca3af">🖼️ Image non disponible</text></svg>';
                }}
              />
            )}
            
            {selectedStatut.type_statut === 'video' && selectedStatut.url_fichier && (
              <video 
                src={selectedStatut.url_fichier} 
                controls 
                autoPlay
                className="w-full h-full object-contain"
              />
            )}
            
            {selectedStatut.type_statut === 'texte' && (
              <div className="w-full h-full flex items-center justify-center bg-black/90 p-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl text-white">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-gradient-to-br from-blue-400 to-purple-400 flex items-center justify-center text-white text-lg font-bold overflow-hidden">
                      {selectedStatut.photo_profil ? (
                        <img src={selectedStatut.photo_profil} alt="" className="w-full h-full object-cover" />
                      ) : (
                        selectedStatut.prenom?.charAt(0).toUpperCase()
                      )}
                    </div>
                    <div>
                      <p className="font-semibold">{selectedStatut.prenom} {selectedStatut.nom}</p>
                      <p className="text-sm text-gray-400">📍 {selectedStatut.distance_km || 0} km</p>
                    </div>
                  </div>
                  <p className="text-xl leading-relaxed">{selectedStatut.contenu}</p>
                </div>
              </div>
            )}
            
            {/* Footer avec infos et bouton discuter */}
            <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
              <div className="flex items-center justify-between text-white max-w-6xl mx-auto">
                <div className="flex items-center gap-2 text-sm">
                  <span>👁️ {selectedStatut.nb_vues || 0} vues</span>
                  <span>•</span>
                  <span>📍 {selectedStatut.distance_km || 0} km</span>
                </div>
                {selectedStatut.id_utilisateur !== currentUser.id_utilisateur && (
                  <button
                    onClick={() => {
                      setSelectedStatut(null);
                      startDiscussion(selectedStatut.id_utilisateur);
                    }}
                    className="bg-blue-600 px-4 py-2 rounded-full text-sm font-medium hover:bg-blue-700 transition flex items-center gap-2"
                  >
                    💬 Discuter
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}